/**
 * App Module
 * Manages application state, UI state, and global settings
 */

import { saveSelectedLocation } from "@/services/localStorageService";

export const app = {
  namespaced: true,
  state: {
    // Loading states
    isLoading: false,
    configLoader: false,
    fetchingData: false,

    // UI state
    showSidebar: false,
    selectedItem: null,
    selectedLocation: null,
    isLocationModalOpen: false,
    isScheduleModalOpen: false,
    isRestaurantClosedModalOpen: false,
    isCartOpen: false,
    isCartPinned: false,
    selectedCategoryId: null,

    // Opciones para configurar el OrderConfigModal
    scheduleModalOptions: {
      showAddress: true,
      showSchedule: true
    },

    // Search
    searchQuery: "",

    // Config
    config: {
      dinner_option: "pickup"
    },
    orderSettings: {},

    // Toast notifications
    toast: {
      visible: false,
      title: "",
      message: "",
      variant: "info"
    }
  },
  getters: {
    // Loading states
    getIsLoading: (state) => state.isLoading,
    configLoader: (state) => state.configLoader,
    getFetchingData: (state) => state.fetchingData,

    // UI state
    showSidebar: (state) => state.showSidebar,
    getSelectedItem: (state) => state.selectedItem,
    getSelectedLocation: (state) => state.selectedLocation,
    selectedLocation: (state) => state.selectedLocation,
    isLocationModalOpen: (state) => state.isLocationModalOpen,
    isScheduleModalOpen: (state) => state.isScheduleModalOpen,
    isRestaurantClosedModalOpen: (state) => state.isRestaurantClosedModalOpen,
    getScheduleModalOptions: (state) => state.scheduleModalOptions,
    isCartOpen: (state) => state.isCartOpen,
    isCartPinned: (state) => state.isCartPinned,
    getSelectedCategoryId: (state) => state.selectedCategoryId,
    getScheduleModalOptions: (state) => state.scheduleModalOptions,

    // Search
    getSearchQuery: (state) => state.searchQuery,

    // Config
    getConfig: (state) => state.config,
    getOrderSettings: (state) => state.orderSettings,

    // Toast notifications
    getToast: (state) => state.toast
  },
  mutations: {
    // Loading states
    setIsLoading(state, payload) {
      state.isLoading = payload;
    },
    TOGGLE_CONFIG_LOADER(state, toggle) {
      state.configLoader = toggle;
    },
    SET_FETCHING_DATA(state, payload) {
      state.fetchingData = payload;
    },

    // UI state
    toggleSidebar(state) {
      state.showSidebar = !state.showSidebar;
    },
    SET_SELECTED_ITEM(state, item) {
      state.selectedItem = item;
    },
    SET_SELECTED_LOCATION(state, location) {
      state.selectedLocation = location;
    },
    SET_LOCATION_MODAL_OPEN(state, isOpen) {
      state.isLocationModalOpen = isOpen;
    },
    /**
     * Set modal open state. Restores default options if closing.
     * @param {boolean} isOpen - true to open, false to close
     */
    SET_SCHEDULE_MODAL_OPEN(state, isOpen) {
      state.isScheduleModalOpen = isOpen;

      // Restablecer opciones de visualización a valores por defecto al cerrar
      if (!isOpen) {
        state.scheduleModalOptions = {
          showAddress: true,
          showSchedule: true
        };
      }
    },
    SET_RESTAURANT_CLOSED_MODAL_OPEN(state, isOpen) {
      state.isRestaurantClosedModalOpen = isOpen;
    },
    SET_CART_OPEN(state, isOpen) {
      state.isCartOpen = isOpen;
    },
    SET_CART_PINNED(state, isPinned) {
      state.isCartPinned = isPinned;
    },
    SET_SELECTED_CATEGORY(state, categoryId) {
      state.selectedCategoryId = categoryId;
    },

    SET_SCHEDULE_MODAL_OPTIONS(state, options) {
      state.scheduleModalOptions = {
        ...state.scheduleModalOptions,
        ...options
      };
    },

    // Search
    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query;
    },

    // Config
    SET_CONFIG(state, newConfig) {
      state.config = newConfig;
    },
    SET_ORDER_SETTINGS(state, newSettings) {
      state.orderSettings = newSettings;
    },

    // Toast notifications
    SHOW_TOAST(state, { title, message, variant = "info" }) {
      state.toast = {
        visible: true,
        title: title || "",
        message: message || "",
        variant: variant
      };
    },
    HIDE_TOAST(state) {
      state.toast.visible = false;
    }
  },
  actions: {
    setIsLoading({ commit }, payload) {
      commit("setIsLoading", payload);
    },
    toggleConfigLoader({ commit, state }) {
      commit("TOGGLE_CONFIG_LOADER", !state.configLoader);
    },
    setFetchingData({ commit }, payload) {
      commit("SET_FETCHING_DATA", payload);
    },
    toggleSidebar({ commit }) {
      commit("toggleSidebar");
    },
    setSelectedItem({ commit }, item) {
      commit("SET_SELECTED_ITEM", item);
    },
    /**
     * Sets selected location and saves to localStorage
     * Also updates selectedStore module (single source of truth)
     */
    setSelectedLocation({ commit, dispatch, rootGetters }, location) {
      if (!location) {
        console.warn("setSelectedLocation: location is null or undefined");
        return;
      }

      // Update selectedStore module first (single source of truth)
      dispatch("selectedStore/setSelectedLocation", location, {
        root: true
      }).catch(() => {
        // If selectedStore module is not available, just update this module
        // This allows backward compatibility during migration
      });

      // Also update this module for backward compatibility
      commit("SET_SELECTED_LOCATION", location);

      // Update document title
      const storeName = location.name;
      const city = location.city || "";
      const state = location.state || "";
      const locationLabel = [city, state].filter(Boolean).join(", ");
      document.title =
        storeName && storeName.toString().trim()
          ? locationLabel
            ? `${storeName} - ${locationLabel} | Online Ordering | Biyo POS`
            : `${storeName} | Online Ordering | Biyo POS`
          : "Online Ordering | Biyo POS";

      // Save to localStorage if location has id
      if (location && location.id) {
        const storeAlias =
          rootGetters.getStoreAlias ||
          rootGetters["store/getStoreAlias"] ||
          window.location.pathname
            .split("/")
            .filter((p) => p)
            .pop() ||
          "default";
        saveSelectedLocation(storeAlias, location);
      } else {
        console.warn("setSelectedLocation: location has no id:", location);
      }
    },

    setLocationModalOpen({ commit }, isOpen) {
      commit("SET_LOCATION_MODAL_OPEN", isOpen);
    },
    setScheduleModalOpen({ commit }, { isOpen, options = {} }) {
      commit("SET_SCHEDULE_MODAL_OPEN", isOpen);

      if (!isOpen) {
        commit("SET_SCHEDULE_MODAL_OPTIONS", {
          showAddress: true,
          showSchedule: true
        });
      } else {
        commit("SET_SCHEDULE_MODAL_OPTIONS", options);
      }
    },
    setRestaurantClosedModalOpen({ commit }, isOpen) {
      commit("SET_RESTAURANT_CLOSED_MODAL_OPEN", isOpen);
    },

    /**
     * Acción para abrir el modal y configurar las secciones a mostrar.
     * @param {Object} context - Vuex context
     * @param {Object} options - { showAddress: boolean, showSchedule: boolean }
     */
    openScheduleModal({ commit }, options = {}) {
      commit("SET_SCHEDULE_MODAL_OPTIONS", options);
      commit("SET_SCHEDULE_MODAL_OPEN", true);
    },

    setCartOpen({ commit }, isOpen) {
      commit("SET_CART_OPEN", isOpen);
    },
    setCartPinned({ commit }, isPinned) {
      commit("SET_CART_PINNED", isPinned);
    },
    setSelectedCategory({ commit }, categoryId) {
      commit("SET_SELECTED_CATEGORY", categoryId);
    },
    setSearchQuery({ commit }, query) {
      commit("SET_SEARCH_QUERY", query);
    },
    setConfig({ commit }, payload) {
      commit("SET_CONFIG", payload);
    },
    setOrderSettings({ commit }, payload) {
      commit("SET_ORDER_SETTINGS", payload);
    },

    // Toast notifications
    showToast({ commit }, { title, message, variant = "info" }) {
      commit("SHOW_TOAST", { title, message, variant });
    },
    hideToast({ commit }) {
      commit("HIDE_TOAST");
    }
  }
};
